"use client";

import type { JSX } from "react";
import { QuestionRenderer, Text, View } from "@/components";
import { useLayout } from "@/providers";
import type { AttemptQuestion, QuestionType } from "@/types/elearning";

/**
 * Denominador común entre una pregunta ya guardada (`Question`) y el borrador del
 * editor (`QuestionPayload`): ambos lo satisfacen estructuralmente, así que el
 * preview sirve igual en el listado del banco y dentro del alta/edición.
 */
export interface QuestionPreviewData {
  type: QuestionType;
  statement: string;
  imageUrl?: string | null;
  explanation?: string | null;
  options: Array<{ text: string; isCorrect: boolean }>;
}

export interface QuestionPreviewProps {
  question: QuestionPreviewData;
}

/** El renderer exige onChange; en modo `disabled` nunca dispara. */
const noop = () => {};

/**
 * @name QuestionPreview
 * @description Vista previa de una pregunta tal como la ve el alumno. Reusa el
 * `QuestionRenderer` del intento (misma maqueta que el quiz real, imposible que
 * diverja) en modo lectura, con las opciones correctas pasadas como selección
 * para que el autor las verifique de un vistazo, más la explicación del feedback.
 *
 * @param {QuestionPreviewProps} props - Pregunta guardada o borrador del editor.
 * @returns {JSX.Element} La pregunta renderizada como la vería el alumno.
 */
const QuestionPreview = ({ question }: QuestionPreviewProps): JSX.Element => {
  const { t } = useLayout();

  const sinContenido =
    !question.statement.trim() &&
    question.options.every((opcion) => !opcion.text.trim());

  if (sinContenido) {
    return (
      <Text className="text-sm text-muted-foreground">
        {t("previewEmptyNote")}
      </Text>
    );
  }

  // Ids posicionales: el preview no persiste nada y el borrador del editor
  // todavía no tiene ids del backend.
  const pregunta: AttemptQuestion = {
    questionId: "preview",
    type: question.type,
    statement: question.statement,
    imageUrl: question.imageUrl ?? null,
    options: question.options.map((opcion, i) => ({
      optionId: String(i),
      text: opcion.text
    }))
  };

  const correct = question.options.flatMap((opcion, i) =>
    opcion.isCorrect ? [String(i)] : []
  );

  return (
    <View className="flex flex-col gap-3">
      <Text className="text-xs text-muted-foreground">
        {t("previewStudentViewNote")}
      </Text>
      <View className="box p-4 rounded-xl border border-slate-200 dark:border-slate-800">
        <QuestionRenderer
          pregunta={pregunta}
          seleccion={correct}
          onChange={noop}
          disabled
        />
      </View>
      {question.explanation?.trim() && (
        <View className="flex flex-col gap-1 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
          <Text className="text-xs font-medium text-foreground">
            {t("explanation")}
          </Text>
          <Text className="text-xs text-muted-foreground">
            {question.explanation}
          </Text>
        </View>
      )}
    </View>
  );
};

export default QuestionPreview;
